import { Handle, Position } from '@xyflow/react'
import { Brain, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GraphNodeData } from '@/types/graph'
import { useCanvasStore } from '@store/canvas'

interface LLMNodeProps {
  data: GraphNodeData
  selected?: boolean
  id: string
}

export default function LLMNode({ data, selected, id }: LLMNodeProps) {
  const { t } = useTranslation()
  const removeNode = useCanvasStore((state) => state.removeNode)

  const promptPreview = data.config?.prompt
    ? String(data.config.prompt).substring(0, 80)
    : t('build.noPromptConfigured')

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeNode(id)
  }

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 bg-white shadow-md min-w-48 transition-all ${
        selected
          ? 'border-agent-violet ring-2 ring-agent-violet/30 shadow-lg'
          : 'border-agent-violet/50 hover:border-agent-violet'
      }`}
    >
      <Handle type="target" position={Position.Top} />

      {/* Header */}
      <div className="flex items-center justify-between mb-2 group">
        <div className="flex items-center gap-2 flex-1">
          <Brain size={16} className="text-agent-violet flex-shrink-0" />
          <div className="font-bold text-sm text-gray-900 truncate">
            {data.label || 'LLM'}
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded text-red-600"
          title={t('build.deleteNode')}
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="text-xs text-gray-600 mb-2 bg-gray-50 rounded p-2">
        <div className="truncate text-gray-700">{promptPreview}</div>
      </div>

      {/* Footer */}
      <div className="flex gap-2 text-xs">
        {data.config?.temperature && (
          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
            T:{Number(data.config.temperature as unknown).toFixed(1)}
          </span>
        )}
        {data.config?.max_tokens && (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
            {String(data.config.max_tokens)} {t('build.maxTokens').toLowerCase()}
          </span>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}
